import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  LogOut, 
  TrendingUp, 
  TrendingDown,
  Calendar,
  Filter,
  BarChart3,
  RefreshCw,
  Banknote,
  PiggyBank,
  Target,
  PieChart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { format, subYears, startOfYear } from 'date-fns';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  ComposedChart,
  Line,
  PieChart as RechartsPie,
  Pie,
  Cell
} from 'recharts';
import { ScrollArea } from '../components/ui/scroll-area';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format with commas and 2 decimal places for tooltips
const formatCurrencyFull = (value) => {
  return `R${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Compact format for KPI cards and axes
const formatCurrencyCompact = (value) => {
  if (value >= 1e9) return `R${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `R${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `R${(value / 1e3).toFixed(0)}K`;
  return `R${value.toFixed(0)}`;
};

const formatPercent = (value) => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

const getReturnColorClass = (value) => {
  if (value > 1.1) return 'return-pos-6';
  if (value > 1.0) return 'return-pos-5';
  if (value > 0.95) return 'return-pos-4';
  if (value > 0.9) return 'return-pos-3';
  if (value > 0.8) return 'return-pos-2';
  if (value > 0) return 'return-pos-1';
  if (value === 0) return 'return-neutral';
  if (value > -1) return 'return-neg-1';
  if (value > -3) return 'return-neg-2';
  return 'return-neg-3';
};

// Colors for sector allocation pie chart
const SECTOR_COLORS = [
  '#1d2d2a', '#2a4c45', '#315c52', '#18a37c', '#bc9d6e',
  '#4a7c6f', '#5a9485', '#6aac9b', '#7ac4b1', '#8adcc7',
  '#d4a76a', '#c49454', '#b4813e', '#a46e28', '#945b12',
  '#3d6b5e', '#507f72', '#639386', '#76a79a', '#89bbae',
  '#e0be8a', '#d0ab74', '#c0985e', '#b08548', '#a07232'
];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-[#1d2d2a] rounded-md shadow-lg p-3 text-sm" data-testid="chart-tooltip">
      <p className="font-semibold text-slate-700 mb-2">{label}</p>
      {payload.map((entry, i) => {
        let formattedValue;
        if (entry.dataKey === 'yield') {
          formattedValue = `${Number(entry.value).toFixed(2)}%`;
        } else {
          formattedValue = formatCurrencyFull(entry.value);
        }
        return (
          <p key={i} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: entry.color }} />
            <span className="text-slate-500">{entry.name}:</span>
            <span className="font-mono font-semibold text-slate-900">{formattedValue}</span>
          </p>
        );
      })}
      {/* Show total NAV if both cash and loans present */}
      {payload.length >= 2 && payload[0].dataKey !== 'yield' && payload[1].dataKey !== 'yield' && (
        <p className="flex items-center gap-2 pt-1 mt-1 border-t border-slate-200">
          <span className="w-3 h-3 rounded-sm inline-block bg-transparent" />
          <span className="text-slate-500">Total NAV:</span>
          <span className="font-mono font-semibold text-slate-900">
            {formatCurrencyFull((payload.find(p => p.dataKey === 'cash')?.value || 0) + (payload.find(p => p.dataKey === 'loans')?.value || 0))}
          </span>
        </p>
      )}
    </div>
  );
};

export default function Dashboard({ onLogout }) {
  const [portfolioData, setPortfolioData] = useState(null);
  const [allSectors, setAllSectors] = useState([]);
  const [excludedSectors, setExcludedSectors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  const fetchSectors = async () => {
    try {
      const response = await axios.get(`${API}/portfolio/sectors`);
      setAllSectors(response.data.sectors || []);
    } catch (error) {
      toast.error('Failed to load sectors');
    }
  };

  const fetchPortfolioData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/portfolio/data`, {
        start_date: dateRange.start ? format(dateRange.start, 'yyyy-MM-dd') : null,
        end_date: dateRange.end ? format(dateRange.end, 'yyyy-MM-dd') : null,
        excluded_sectors: excludedSectors
      });
      setPortfolioData(response.data);
    } catch (error) {
      toast.error('Failed to load portfolio data');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange.start, dateRange.end, excludedSectors]);

  useEffect(() => { fetchSectors(); }, []);
  useEffect(() => { fetchPortfolioData(); }, [fetchPortfolioData]);

  const handleSectorToggle = (sector) => {
    setExcludedSectors(prev =>
      prev.includes(sector) ? prev.filter(s => s !== sector) : [...prev, sector]
    );
  };

  const selectAllSectors = () => setExcludedSectors([]);
  const deselectAllSectors = () => setExcludedSectors([...allSectors]);

  const setPresetRange = (preset) => {
    const now = new Date();
    let start;
    switch (preset) {
      case 'YTD': start = startOfYear(now); break;
      case '1Y': start = subYears(now, 1); break;
      case '3Y': start = subYears(now, 3); break;
      default: start = null;
    }
    setDateRange({ start, end: preset === 'ALL' ? null : now });
  };

  // Prepare stacked area chart data
  const chartData = useMemo(() => {
    if (!portfolioData?.daily_data) return [];
    const data = portfolioData.daily_data;
    const step = Math.max(1, Math.floor(data.length / 200));
    return data.filter((_, i) => i % step === 0 || i === data.length - 1).map(d => ({
      date: d.date,
      displayDate: format(new Date(d.date), 'MMM yy'),
      cash: d.cash_balance,
      loans: d.total_loans,
      yield: d.avg_yield * 100,
    }));
  }, [portfolioData]);

  // Monthly returns grid
  const monthlyReturnsGrid = useMemo(() => {
    if (!portfolioData?.monthly_returns) return { years: [], data: {} };
    const years = [...new Set(portfolioData.monthly_returns.map(r => r.year))].sort();
    const data = {};
    years.forEach(year => { data[year] = Array(12).fill(null); });
    portfolioData.monthly_returns.forEach(r => {
      if (data[r.year]) data[r.year][r.month - 1] = r.return_pct;
    });
    return { years, data };
  }, [portfolioData]);

  // Sector allocation pie data
  const pieData = useMemo(() => {
    if (!portfolioData?.sector_allocations) return [];
    return portfolioData.sector_allocations.map(sa => ({
      name: sa.sector.replace(/_/g, ' '),
      value: sa.value,
      percentage: sa.percentage,
    }));
  }, [portfolioData]);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (isLoading && !portfolioData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="h-8 w-8 text-[#006633] animate-spin" />
          <p className="text-slate-500">Loading portfolio data...</p>
        </div>
      </div>
    );
  }

  const metrics = portfolioData?.metrics;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="dashboard">
      {/* Header */}
      <header className="bg-[#1d2d2a] border-b border-slate-700 h-16 flex items-center justify-between px-6 sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <img src="/ptc-logo.svg" alt="PTC Logo" className="h-10 w-10" />
          <span className="text-xl font-bold text-[#bc9d6e] font-heading">
            Plane Tree Capital
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-300 hidden sm:block">Investor Dashboard</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            className="text-slate-300 hover:text-white hover:bg-[#2a4c45]"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <main className="flex-1 py-8 px-6 space-y-8 max-w-7xl mx-auto w-full">
        {/* KPI Cards */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4" data-testid="kpi-cards">
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow animate-fade-in">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">NAV</p>
                  <p className="text-xl font-bold text-slate-900 font-mono" data-testid="nav-value">
                    {metrics?.nav ? formatCurrencyCompact(metrics.nav) : '-'}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-full bg-[#1d2d2a]/10 flex items-center justify-center">
                  <Banknote className="h-5 w-5 text-[#1d2d2a]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow animate-fade-in" style={{ animationDelay: '0.05s' }}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">Total Loans</p>
                  <p className="text-xl font-bold text-slate-900 font-mono" data-testid="total-loans">
                    {metrics?.total_loans != null ? formatCurrencyCompact(metrics.total_loans) : '-'}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-full bg-[#243d37] flex items-center justify-center">
                  <PiggyBank className="h-5 w-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">Portfolio Return</p>
                  <p className={`text-xl font-bold font-mono ${metrics?.annualized_return >= 0 ? 'text-[#18a37c]' : 'text-red-600'}`} data-testid="annualized-return">
                    {metrics ? formatPercent(metrics.annualized_return) : '-'}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">incl. cash</p>
                </div>
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${metrics?.annualized_return >= 0 ? 'bg-[#2a4c45]' : 'bg-red-50'}`}>
                  {metrics?.annualized_return >= 0 ?
                    <TrendingUp className="h-5 w-5 text-white" /> :
                    <TrendingDown className="h-5 w-5 text-red-600" />
                  }
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow animate-fade-in" style={{ animationDelay: '0.15s' }}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">Loan Book Return</p>
                  <p className={`text-xl font-bold font-mono ${metrics?.loan_book_annualized >= 0 ? 'text-[#bc9d6e]' : 'text-red-600'}`} data-testid="loan-book-return">
                    {metrics?.loan_book_annualized != null ? formatPercent(metrics.loan_book_annualized) : '-'}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">excl. cash drag</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-[#bc9d6e]/20 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-[#bc9d6e]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">Avg Yield</p>
                  <p className="text-xl font-bold text-[#bc9d6e] font-mono" data-testid="avg-yield">
                    {metrics?.avg_yield != null ? `${(metrics.avg_yield * 100).toFixed(2)}%` : '-'}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-full bg-[#bc9d6e]/20 flex items-center justify-center">
                  <Target className="h-5 w-5 text-[#bc9d6e]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow animate-fade-in" style={{ animationDelay: '0.25s' }}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">Cash</p>
                  <p className="text-xl font-bold text-slate-900 font-mono" data-testid="cash-balance">
                    {metrics?.cash_balance != null ? formatCurrencyCompact(metrics.cash_balance) : '-'}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Chart and Filters */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Performance Chart */}
          <Card className="lg:col-span-8 bg-white border-slate-200 shadow-sm" data-testid="performance-chart">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-[#006633]" />
                  Portfolio Composition & Yield
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  {['YTD', '1Y', '3Y', 'ALL'].map(p => (
                    <Button key={p} variant="outline" size="sm" onClick={() => setPresetRange(p)} className="text-xs" data-testid={`preset-${p.toLowerCase()}`}>{p}</Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {/* Date Range Pickers */}
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="text-sm" data-testid="start-date-picker">
                        {dateRange.start ? format(dateRange.start, 'MMM dd, yyyy') : 'Start Date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={dateRange.start}
                        onSelect={(date) => {
                          setDateRange(prev => ({ ...prev, start: date }));
                          setStartDateOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <span className="text-slate-400">to</span>
                  <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="text-sm" data-testid="end-date-picker">
                        {dateRange.end ? format(dateRange.end, 'MMM dd, yyyy') : 'End Date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={dateRange.end}
                        onSelect={(date) => {
                          setDateRange(prev => ({ ...prev, end: date }));
                          setEndDateOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                {isLoading && <RefreshCw className="h-4 w-4 text-[#1d2d2a] animate-spin" />}
              </div>

              {/* Stacked Area Chart */}
              <div className="h-[380px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <defs>
                      <linearGradient id="colorCash" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#bc9d6e" stopOpacity={0.6}/>
                        <stop offset="95%" stopColor="#bc9d6e" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorLoans" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1d2d2a" stopOpacity={0.7}/>
                        <stop offset="95%" stopColor="#1d2d2a" stopOpacity={0.2}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="displayDate"
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickLine={false}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 11, fill: '#1d2d2a' }}
                      tickLine={false}
                      axisLine={{ stroke: '#1d2d2a' }}
                      tickFormatter={formatCurrencyCompact}
                      width={80}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 11, fill: '#18a37c' }}
                      tickLine={false}
                      axisLine={{ stroke: '#18a37c' }}
                      tickFormatter={(v) => `${v.toFixed(2)}%`}
                      domain={['auto', 'auto']}
                      width={55}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="loans"
                      name="Deployed Assets"
                      stackId="nav"
                      stroke="#1d2d2a"
                      strokeWidth={1}
                      fill="url(#colorLoans)"
                    />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="cash"
                      name="Cash"
                      stackId="nav"
                      stroke="#bc9d6e"
                      strokeWidth={1}
                      fill="url(#colorCash)"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="yield"
                      name="Avg Yield"
                      stroke="#18a37c"
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Sector Filters */}
          <Card className="lg:col-span-4 bg-white border-slate-200 shadow-sm h-fit lg:sticky lg:top-24" data-testid="sector-filters">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Filter className="h-5 w-5 text-[#006633]" />
                Sector Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex gap-2 mb-4">
                <Button variant="outline" size="sm" onClick={selectAllSectors} className="text-xs flex-1" data-testid="select-all-sectors">Select All</Button>
                <Button variant="outline" size="sm" onClick={deselectAllSectors} className="text-xs flex-1" data-testid="deselect-all-sectors">Deselect All</Button>
              </div>
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-3">
                  {allSectors.sort().map((sector) => (
                    <div key={sector} className="flex items-center space-x-3">
                      <Checkbox
                        id={`sector-${sector}`}
                        checked={!excludedSectors.includes(sector)}
                        onCheckedChange={() => handleSectorToggle(sector)}
                        className="data-[state=checked]:bg-[#006633] data-[state=checked]:border-[#006633]"
                        data-testid={`sector-checkbox-${sector.replace(/\s+/g, '-').toLowerCase()}`}
                      />
                      <Label htmlFor={`sector-${sector}`} className="text-sm text-slate-700 cursor-pointer leading-none">
                        {sector.replace(/_/g, ' ')}
                      </Label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-500">
                  {allSectors.length - excludedSectors.length} of {allSectors.length} sectors selected
                </p>
                {excludedSectors.length > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Excluded capital earns 6.50% p.a. as cash
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Sector Allocation Chart */}
        {pieData.length > 0 && (
          <Card className="bg-white border-slate-200 shadow-sm" data-testid="sector-allocation">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <PieChart className="h-5 w-5 text-[#006633]" />
                Sector Allocation
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        innerRadius={50}
                        paddingAngle={1}
                        label={({ name, percentage }) => percentage > 3 ? `${name} ${percentage.toFixed(1)}%` : ''}
                        labelLine={false}
                      >
                        {pieData.map((_, idx) => (
                          <Cell key={idx} fill={SECTOR_COLORS[idx % SECTOR_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [formatCurrencyFull(value), name]}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #1d2d2a',
                          borderRadius: '6px',
                        }}
                      />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                <div>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                    {pieData.map((item, idx) => (
                      <div key={item.name} className="flex items-center justify-between text-sm py-1 border-b border-slate-50">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: SECTOR_COLORS[idx % SECTOR_COLORS.length] }} />
                          <span className="text-slate-700">{item.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-mono text-slate-500 text-xs">{formatCurrencyCompact(item.value)}</span>
                          <span className="font-mono font-semibold text-slate-900 w-14 text-right">{item.percentage.toFixed(1)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Monthly Returns Heatmap */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="monthly-returns">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold text-slate-900">Monthly Returns (%)</CardTitle>
          </CardHeader>
          <CardContent className="p-6 overflow-x-auto">
            <table className="returns-table w-full min-w-[800px]">
              <thead>
                <tr>
                  <th className="text-left">Year</th>
                  {months.map(m => <th key={m}>{m}</th>)}
                  <th>Annual</th>
                </tr>
              </thead>
              <tbody>
                {monthlyReturnsGrid.years.map(year => {
                  const annualReturn = portfolioData?.annual_returns?.find(r => r.year === year);
                  return (
                    <tr key={year}>
                      <td className="font-semibold text-slate-700 text-left">{year}</td>
                      {monthlyReturnsGrid.data[year].map((ret, idx) => (
                        <td
                          key={`${year}-${idx}`}
                          className={`font-mono text-sm ${ret !== null ? getReturnColorClass(ret) : ''}`}
                        >
                          {ret !== null ? ret.toFixed(2) : '-'}
                        </td>
                      ))}
                      <td className={`font-mono text-sm font-semibold ${annualReturn ? getReturnColorClass(annualReturn.return_pct) : ''}`}>
                        {annualReturn ? annualReturn.return_pct.toFixed(2) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Annual Returns Summary */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="annual-returns">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold text-slate-900">Annual Returns Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {portfolioData?.annual_returns?.map(({ year, return_pct }) => (
                <div key={year} className="p-4 rounded-md border border-slate-200 bg-slate-50 text-center">
                  <p className="text-sm font-medium text-slate-500 mb-1">{year}</p>
                  <p className={`text-xl font-bold font-mono ${return_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatPercent(return_pct)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <footer className="text-center py-6 text-xs text-slate-400">
          <p>&copy; {new Date().getFullYear()} Plane Tree Capital (Pty) Ltd. All rights reserved.</p>
          <p className="mt-1">Authorised Financial Services Provider (FSP 51788)</p>
        </footer>
      </main>
    </div>
  );
}
